// =============================================================================
// HERO SECTION COMPONENT (V2 - NORMALIZED)
// =============================================================================
// Refactored to use discriminated union types and centralized action handler

import React from 'react';
import { Button } from '@/components/Button';
import { createActionHandler, convertButtonToAction } from '@/utils/actionHandler';
import { layoutToClasses, displayToStyles } from '@/types/layout';
import type { ContentComponent } from '@/types';
import type { HeroComponent } from '@/types/components';

interface HeroSectionProps {
  component: ContentComponent;
}

/**
 * Type guard to check if component is new HeroComponent format
 */
function isNewHeroFormat(component: ContentComponent): boolean {
  return (
    component.type === 'hero' &&
    'content' in component &&
    typeof (component as any).content === 'object' &&
    'title' in (component as any).content
  );
}

/**
 * Hero Section Component
 * Supports both old and new component formats during migration
 */
export const HeroSection: React.FC<HeroSectionProps> = ({ component }) => {
  // Check if new format
  if (isNewHeroFormat(component)) {
    return <HeroSectionNew component={component as unknown as any} />;
  }
  
  // Fall back to old format
  return <HeroSectionLegacy component={component} />;
};

/**
 * New format Hero Section
 */
const HeroSectionNew: React.FC<{ component: HeroComponent }> = ({ component }) => {
  const { id, content, media, actions, layout, display } = component;
  
  // Generate CSS classes from layout config
  const layoutClasses = layoutToClasses(layout);
  
  // Generate inline styles from display config
  const displayStyles = displayToStyles(display);
  
  // Determine if this is a full-width background hero
  const isFullWidthBg = layout.fullWidth && media?.image && display.background;
  
  if (isFullWidthBg) {
    return (
      <section
        id={id}
        className={`hero hero-fullwidth-bg ${layoutClasses}`}
        style={displayStyles}
      >
        {/* Overlay */}
        {display.background?.overlay && (
          <div
            className="hero-overlay"
            style={{
              backgroundColor: display.background.overlay.color || 'rgba(0, 0, 0, 0.5)',
              opacity: display.background.overlay.opacity || 0.5,
            }}
          />
        )}
        
        {/* Content */}
        <div className="hero-content hero-content-centered">
          <div className="hero-text">
            {content.title && <h1>{content.title}</h1>}
            {content.subtitle && <h2 className="hero-subtitle subheading">{content.subtitle}</h2>}
            {content.body && <p>{content.body}</p>}
            
            {actions.length > 0 && (
              <div className="hero-actions">
                {actions.map((action, index) => (
                  <Button
                    key={action.id || index}
                    variant={action.variant || 'primary'}
                    size={action.size || 'lg'}
                    onClick={createActionHandler(action)}
                    disabled={action.disabled}
                  >
                    {action.label}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    );
  }
  
  // Default layout with image on the side
  return (
    <section id={id} className={`hero ${layoutClasses}`} style={displayStyles}>
      <div className="container">
        <div className="hero-content">
          {media?.image && (
            <div className="hero-image">
              <img
                src={media.image}
                alt={media.alt || content.title}
                loading="lazy"
              />
              {media.caption && (
                <p className="hero-image-caption">{media.caption}</p>
              )}
            </div>
          )}
          
          <div className="hero-text">
            {content.title && <h1>{content.title}</h1>}
            {content.subtitle && <h2 className="hero-subtitle subheading">{content.subtitle}</h2>}
            {content.body && <p>{content.body}</p>}
            
            {actions.length > 0 && (
              <div className="hero-actions">
                {actions.map((action, index) => (
                  <Button
                    key={action.id || index}
                    variant={action.variant || 'primary'}
                    size={action.size || 'lg'}
                    onClick={createActionHandler(action)}
                    disabled={action.disabled}
                  >
                    {action.label}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

/**
 * Legacy format Hero Section (backward compatibility)
 * Converts old format to new format on the fly
 */
const HeroSectionLegacy: React.FC<{ component: ContentComponent }> = ({ component }) => {
  const { id, title, content, metadata } = component;
  
  const buttons = metadata?.buttons || [];
  const image = metadata?.image;
  const fullWidth = component.fullWidth || false;
  
  // Convert old ButtonConfig to new ActionConfig
  const actions = buttons.map(convertButtonToAction);
  
  // Build display styles from old metadata
  const displayStyles: React.CSSProperties = {};
  if (image && fullWidth) {
    displayStyles.backgroundImage = `url(${image})`;
    displayStyles.backgroundSize = 'cover';
    displayStyles.backgroundPosition = 'center';
    displayStyles.backgroundRepeat = 'no-repeat';
  }
  
  if (fullWidth && image) {
    return (
      <section
        id={id}
        className="hero hero-fullwidth-bg"
        style={displayStyles}
      >
        <div className="hero-overlay">
          <div className="hero-content hero-content-centered">
            <div className="hero-text">
              <h1>{title}</h1>
              <p>{content}</p>
              
              {actions.length > 0 && (
                <div className="hero-actions">
                  {actions.map((action, index) => (
                    <Button
                      key={index}
                      variant={action.variant || 'primary'}
                      onClick={createActionHandler(action)}
                    >
                      {action.label}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }
  
  return (
    <section id={id} className="hero">
      <div className="container">
        <div className="hero-content">
          {image && (
            <div className="hero-image">
              <img src={image} alt={title} loading="lazy" />
            </div>
          )}
          
          <div className="hero-text">
            <h1>{title}</h1>
            <p>{content}</p>
            
            {actions.length > 0 && (
              <div className="hero-actions">
                {actions.map((action, index) => (
                  <Button
                    key={index}
                    variant={action.variant || 'primary'}
                    onClick={createActionHandler(action)}
                  >
                    {action.label}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Export both versions
export { HeroSectionNew, HeroSectionLegacy };

